/* Header-large background video — poster is LCP; iframe src is set only after window load
 * (and after WP Rocket delayed scripts actually run).
 *
 * Event `playProgress` was renamed to `timeupdate`; prefer Player over raw postMessage. */
const paused = [];
const vimeoPlayers = [];
const revealTimers = [];

/**
 * Rocket-delayed scripts often run after `load` already fired.
 *
 * @param {Function} callback
 */
function strlOnWindowLoad(callback) {
	if (document.readyState === 'complete') {
		callback();
		return;
	}
	window.addEventListener('load', callback, { once: true });
}

/**
 * Skip autoplay on constrained devices / networks.
 * Manual play via the pause button still works.
 *
 * @returns {Promise<boolean>}
 */
async function shouldSkipAutoplay() {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return true;
	}

	const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
	if (connection) {
		if (connection.saveData) {
			return true;
		}
		if (['slow-2g', '2g'].includes(connection.effectiveType)) {
			return true;
		}
	}

	if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 2) {
		return true;
	}
	if (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 2) {
		return true;
	}

	if (typeof navigator.getBattery === 'function') {
		try {
			const battery = await navigator.getBattery();
			if (!battery.charging && battery.level <= 0.2) {
				return true;
			}
		} catch {
			// Battery API blocked / unavailable.
		}
	}

	return false;
}

/**
 * Parse Vimeo start offset from embed URL hash.
 *
 * @param {string} url
 * @returns {number}
 */
function getVimeoStartOffset(url) {
	const match = String(url || '').match(/#t=(\d+)/);
	return match ? parseInt(match[1], 10) : 0;
}

/**
 * Lazy-load the official Vimeo Player SDK (only when a Vimeo header exists).
 *
 * @returns {Promise<typeof window.Vimeo.Player>}
 */
function loadVimeoPlayerApi() {
	if (window.Vimeo?.Player) {
		return Promise.resolve(window.Vimeo.Player);
	}

	return new Promise((resolve, reject) => {
		const existing = document.querySelector('script[data-vimeo-player-api]');
		if (existing) {
			existing.addEventListener('load', () => resolve(window.Vimeo.Player), { once: true });
			existing.addEventListener('error', reject, { once: true });
			return;
		}

		const script = document.createElement('script');
		script.src = 'https://player.vimeo.com/api/player.js';
		script.async = true;
		script.dataset.vimeoPlayerApi = '';
		script.addEventListener('load', () => resolve(window.Vimeo.Player), { once: true });
		script.addEventListener('error', reject, { once: true });
		document.head.appendChild(script);
	});
}

/**
 * Show poster / hide poster over the iframe.
 *
 * @param {HTMLElement|null} poster
 * @param {boolean} show
 */
function setPosterVisible(poster, show) {
	if (!poster) {
		return;
	}
	if (show) {
		poster.classList.remove('opacity-0');
	} else {
		poster.classList.add('opacity-0');
	}
}

/**
 * Keep button UI aligned with the actual playback state.
 *
 * @param {HTMLElement} btn
 * @param {number} key
 * @param {boolean} isPaused
 */
function setPlaybackState(btn, key, isPaused) {
	btn.querySelector('.icon-pause')?.classList.toggle('hidden!', isPaused);
	btn.querySelector('.icon-play')?.classList.toggle('hidden!', !isPaused);
	paused[key] = isPaused;
}

/**
 * Fade poster out once real playback has started (autoplay path).
 *
 * @param {HTMLElement} section
 * @param {HTMLElement} btn
 * @param {number} key
 */
function revealVideo(section, btn, key) {
	setPosterVisible(section.querySelector('[data-poster]'), false);

	if (paused[key]) {
		setPlaybackState(btn, key, false);
	}
}

/**
 * Toggle play/pause from the control button.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {HTMLElement} btn
 * @param {string} videoType
 * @param {number} key
 */
function togglePlay(iframe, btn, videoType, key) {
	window.clearTimeout(revealTimers[key]);

	const section = iframe.closest('section.header-large');
	const willPlay = paused[key];

	if ('youtube' === videoType) {
		iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1 }), '*');
		const func = willPlay ? 'playVideo' : 'pauseVideo';
		iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
	} else if ('vimeo' === videoType) {
		const player = vimeoPlayers[key];
		if (player) {
			willPlay ? player.play() : player.pause();
		} else {
			const func = willPlay ? 'play' : 'pause';
			iframe.contentWindow.postMessage(JSON.stringify({ method: func }), '*');
		}
	}

	if (willPlay) {
		setPosterVisible(section?.querySelector('[data-poster]'), false);
	}

	setPlaybackState(btn, key, !willPlay);
}

/**
 * Resolve header section index for a given iframe message source (YouTube).
 *
 * @param {MessageEvent} e
 * @returns {{ iframe: HTMLIFrameElement, section: HTMLElement, btn: HTMLElement, key: number }|null}
 */
function getVideoContextFromMessage(e) {
	const iframe = Array.from(document.querySelectorAll('section.header-large iframe[data-video]')).find(
		(f) => f.contentWindow === e.source
	);
	if (!iframe) {
		return null;
	}

	const section = iframe.closest('section.header-large');
	const sections = document.querySelectorAll('section.header-large');
	const key = Array.prototype.indexOf.call(sections, section);
	const btn = section?.querySelector('[data-pause]');

	if (!section || !btn || key < 0) {
		return null;
	}

	return { iframe, section, btn, key };
}

/**
 * Register parent window for YouTube iframe postMessage events.
 *
 * @param {HTMLIFrameElement} iframe
 */
function bindYouTubeListening(iframe) {
	iframe.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 1 }), '*');
}

/**
 * YouTube: sync UI once autoplay has actually started (infoDelivery + currentTime).
 *
 * @param {MessageEvent} e
 */
function ytOnReady(e) {
	let data;
	try {
		data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
	} catch {
		return;
	}

	if (!data || data.event !== 'infoDelivery' || !data.info || !(data.info.currentTime > 0)) {
		return;
	}

	const ctx = getVideoContextFromMessage(e);
	if (!ctx) {
		return;
	}

	if (paused[ctx.key]) {
		togglePlay(ctx.iframe, ctx.btn, ctx.iframe.dataset.video, ctx.key);
	}

	const sections = document.querySelectorAll('section.header-large');
	const needsSync = Array.from(sections).some((section, key) => {
		const iframe = section.querySelector('iframe[data-video="youtube"]');
		return iframe && paused[key];
	});

	if (!needsSync) {
		window.removeEventListener('message', ytOnReady);
	}
}

/**
 * Wire official Vimeo Player SDK for one header iframe.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {HTMLElement} section
 * @param {HTMLElement} btn
 * @param {number} key
 */
function initVimeoPlayer(iframe, section, btn, key) {
	const startOffset = getVimeoStartOffset(iframe.dataset.src || iframe.src);

	loadVimeoPlayerApi()
		.then((Player) => {
			const player = new Player(iframe);
			vimeoPlayers[key] = player;

			const onTimeUpdate = (data) => {
				if (data.seconds > startOffset + 0.3) {
					revealVideo(section, btn, key);
					player.off('timeupdate', onTimeUpdate);
				}
			};

			player.on('timeupdate', onTimeUpdate);

			player.on('playing', () => {
				player.getCurrentTime().then((seconds) => {
					if (seconds > startOffset + 0.15) {
						revealVideo(section, btn, key);
						player.off('timeupdate', onTimeUpdate);
					}
				});
			});
		})
		.catch(() => {
			revealTimers[key] = window.setTimeout(() => revealVideo(section, btn, key), 1500);
		});
}

strlOnWindowLoad(async () => {
	const skipAutoplay = await shouldSkipAutoplay();
	const sections = document.querySelectorAll('section.header-large');

	sections.forEach((section, key) => {
		const btn = section.querySelector('[data-pause]');
		const iframe = section.querySelector('iframe[data-video]');
		const videoType = iframe?.dataset.video;

		if (!iframe || !btn) {
			return;
		}

		paused[key] = true;
		setPlaybackState(btn, key, true);

		if ('youtube' === videoType && !skipAutoplay) {
			window.addEventListener('message', ytOnReady);
			iframe.addEventListener('load', () => bindYouTubeListening(iframe), { once: true });
		}

		if (!skipAutoplay && iframe.dataset.src) {
			iframe.src = iframe.dataset.src;
		}

		if ('vimeo' === videoType && !skipAutoplay) {
			initVimeoPlayer(iframe, section, btn, key);
		}

		if (!skipAutoplay) {
			revealTimers[key] = window.setTimeout(() => revealVideo(section, btn, key), 4000);
		}

		btn.addEventListener('click', () => {
			if (!videoType) {
				return;
			}

			if (!iframe.getAttribute('src') && iframe.dataset.src) {
				iframe.src = iframe.dataset.src;

				if ('youtube' === videoType) {
					window.addEventListener('message', ytOnReady);
					iframe.addEventListener('load', () => bindYouTubeListening(iframe), { once: true });
				}

				if ('vimeo' === videoType && !vimeoPlayers[key]) {
					initVimeoPlayer(iframe, section, btn, key);
				}

				revealTimers[key] = window.setTimeout(() => revealVideo(section, btn, key), 4000);
				return;
			}

			togglePlay(iframe, btn, videoType, key);
		});
	});
});
